import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX, ChevronDown } from 'lucide-react';
import mediaLibrary from '../content/mediaLibrary';
import useSEO from '../hooks/useSEO';

/**
 * TikTok-style Gallery:
 * - Year picker tabs (sticky at top)
 * - Vertical snap-scroll feed, one item per viewport
 * - Only the visible video plays (IntersectionObserver); others paused & preload="none"
 * - Images proxied through images.weserv.nl for on-the-fly WebP resizing (~15-20x smaller)
 */

// ---- Image optimization via weserv.nl (free CDN proxy) ----
// Converts a full-res JPEG (3-4 MB) → WebP at retina-sharp quality (~400-700 KB)
const optimizeImg = (url, width = 1600, quality = 92) => {
  // weserv expects url param without protocol
  const stripped = url.replace(/^https?:\/\//, '');
  return `https://images.weserv.nl/?url=${encodeURIComponent(stripped)}&w=${width}&output=webp&q=${quality}&we`;
};

// Tiny blurred placeholder (40px wide) — shown instantly while full image loads
const placeholderImg = (url) => optimizeImg(url, 40, 40);

const buildItems = (year) => {
  const content = mediaLibrary[year] || { videos: [], images: [] };
  const v = content.videos.map((src) => ({ type: 'video', src, year }));
  const i = content.images.map((src) => ({ type: 'image', src, year }));
  // Interleave for pacing (v, i, v, i, …)
  const out = [];
  const max = Math.max(v.length, i.length);
  for (let k = 0; k < max; k++) {
    if (v[k]) out.push(v[k]);
    if (i[k]) out.push(i[k]);
  }
  return out;
};

const GalleryPage = () => {
  const { language, t } = useLanguage();
  useSEO({ title: t.seo.gallery.title, description: t.seo.gallery.description });

  const years = useMemo(
    () => Object.keys(mediaLibrary).map(Number).sort((a, b) => b - a),
    []
  );
  const [activeYear, setActiveYear] = useState(years[0]);
  const [muted, setMuted] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  const items = useMemo(() => buildItems(activeYear), [activeYear]);
  const feedRef = useRef(null);
  const slideRefs = useRef([]);
  const videoRefs = useRef([]);

  // Reset refs on items change
  useEffect(() => {
    slideRefs.current = slideRefs.current.slice(0, items.length);
    videoRefs.current = videoRefs.current.slice(0, items.length);
    setActiveIndex(0);
    if (feedRef.current) feedRef.current.scrollTo({ top: 0, behavior: 'auto' });
  }, [items.length, activeYear]);

  // IntersectionObserver: play only the slide that's >=60% visible
  useEffect(() => {
    const root = feedRef.current;
    if (!root) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Number(entry.target.dataset.index);
          if (Number.isNaN(idx)) return;
          const video = videoRefs.current[idx];
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            setActiveIndex(idx);
            if (video) {
              try {
                video.muted = muted;
                video.play().catch(() => {});
              } catch (e) {
                // Expected under mobile autoplay policies — debug only.
                console.debug('video autoplay blocked', e);
              }
            }
          } else if (video && !video.paused) {
            video.pause();
            try { video.currentTime = 0; } catch (e) { console.debug('video rewind skipped', e); }
          }
        });
      },
      { root, threshold: [0, 0.6, 0.9] }
    );

    slideRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [items, muted]);

  // Sync mute state across all videos live
  useEffect(() => {
    videoRefs.current.forEach((v) => { if (v) v.muted = muted; });
  }, [muted]);

  const jumpToYear = useCallback((year) => {
    setActiveYear(year);
  }, []);

  return (
    <div className="min-h-screen bg-[#1A1A18]" data-testid="gallery-page">
      {/* Stage wrapper — explicit dvh height accounting for floating nav + its top margin */}
      <div
        className="relative flex flex-col"
        style={{
          height: '100dvh',
          paddingTop: '6.5rem', // clear floating nav (4.5rem) + mt-4 (1rem) + breathing
        }}
      >
        {/* Title + Year Tabs (fixed height so we can calc slide size below) */}
        <div className="relative z-20 bg-[#1A1A18] border-b border-white/5 flex-shrink-0" style={{ height: '4rem' }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-12 h-full flex items-center">
            <div className="flex items-center justify-between gap-6 flex-wrap w-full">
              {/* Left: eyebrow + title */}
              <div className="flex items-center gap-4">
                <div className="deco-line" />
                <span className="eyebrow text-[#8A8780]">{t.nav.gallery}</span>
                <h1
                  className="font-display text-xl lg:text-2xl font-normal tracking-tight text-[#F5F0E8] hidden sm:inline"
                  data-testid="gallery-title"
                >
                  {t.gallery.title}
                </h1>
              </div>

              {/* Right: year tabs */}
              <div className="flex items-center gap-1 overflow-x-auto no-scrollbar" data-testid="year-tabs">
                {years.map((y) => (
                  <button
                    key={y}
                    onClick={() => jumpToYear(y)}
                    data-testid={`year-tab-${y}`}
                    className={`relative px-3 lg:px-5 py-2 font-body text-sm tracking-wider transition-all duration-300 whitespace-nowrap ${
                      activeYear === y
                        ? 'text-[#F5F0E8]'
                        : 'text-[#8A8780] hover:text-[#F5F0E8]/80'
                    }`}
                  >
                    {y}
                    {activeYear === y && (
                      <motion.span
                        layoutId="year-underline"
                        className="absolute -bottom-[1px] left-0 right-0 h-[2px] bg-[#A14223]"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* FEED — snap scroll container with EXPLICIT height = dvh - nav+margin(6.5rem) - tabs(4rem) */}
        <div
          ref={feedRef}
          className="relative overflow-y-scroll snap-y snap-mandatory no-scrollbar"
          style={{
            height: 'calc(100dvh - 10.5rem)',
            scrollSnapType: 'y mandatory',
            WebkitOverflowScrolling: 'touch',
          }}
          data-testid="gallery-feed"
        >
          {items.length === 0 && (
            <div className="h-full flex items-center justify-center">
              <p className="eyebrow text-[#8A8780]">
                {language === 'fr' ? 'Aucun contenu' : 'No content'}
              </p>
            </div>
          )}

          <AnimatePresence initial={false}>
            {items.map((item, idx) => (
              <Slide
                key={`${activeYear}-${idx}-${item.src}`}
                item={item}
                index={idx}
                total={items.length}
                active={activeIndex === idx}
                near={Math.abs(activeIndex - idx) <= 2}
                muted={muted}
                language={language}
                registerSlide={(el) => (slideRefs.current[idx] = el)}
                registerVideo={(el) => (videoRefs.current[idx] = el)}
              />
            ))}
          </AnimatePresence>

          {/* Scroll hint overlay - fades after first scroll */}
          <ScrollHint active={activeIndex === 0 && items.length > 1} language={language} />
        </div>

        {/* Right-side slide counter + mute (desktop) */}
        <div className="hidden lg:flex absolute right-6 top-1/2 -translate-y-1/2 z-30 flex-col items-center gap-6" data-testid="gallery-controls">
          <button
            onClick={() => setMuted((m) => !m)}
            className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-[#F5F0E8] hover:bg-white/20 transition-colors duration-200"
            data-testid="mute-toggle"
            aria-label={muted ? (language === 'fr' ? 'Activer le son' : 'Unmute') : (language === 'fr' ? 'Couper le son' : 'Mute')}
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <div className="flex flex-col items-center gap-1 font-body text-xs text-[#F5F0E8]/60 tracking-widest">
            <span className="font-display text-xl text-[#F5F0E8]">{String(activeIndex + 1).padStart(2, '0')}</span>
            <span className="w-px h-6 bg-white/20" />
            <span>{String(items.length).padStart(2, '0')}</span>
          </div>
        </div>

        {/* Mobile floating mute button */}
        <button
          onClick={() => setMuted((m) => !m)}
          className="lg:hidden absolute right-4 bottom-28 z-30 w-11 h-11 rounded-full bg-black/50 backdrop-blur-md border border-white/20 flex items-center justify-center text-[#F5F0E8]"
          data-testid="mute-toggle-mobile"
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Slide — one full-viewport cell in the feed. Centered "stage" on desktop.
// -----------------------------------------------------------------------------
const Slide = ({ item, index, total, active, near, muted, language, registerSlide, registerVideo }) => {
  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <section
      ref={registerSlide}
      data-index={index}
      data-testid={`gallery-slide-${item.year}-${index}`}
      className="snap-start snap-always w-full relative flex items-center justify-center bg-[#0E0E0D]"
      style={{ height: 'calc(100dvh - 9rem)', scrollSnapStop: 'always' }}
    >
      {/* Ambient blurred backdrop (tiny 24px placeholder scaled up = cheap & cinematic) */}
      {item.type === 'image' && (
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center opacity-40 blur-2xl scale-110"
          style={{ backgroundImage: `url(${placeholderImg(item.src)})` }}
        />
      )}

      {/* Stage — full-bleed on mobile, portrait-shaped on desktop */}
      <div
        className="relative z-10 w-full h-full lg:w-auto lg:aspect-[9/16] lg:rounded-xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
        style={{ maxHeight: 'calc(100dvh - 9rem)' }}
      >
        {item.type === 'video' ? (
          <video
            ref={registerVideo}
            src={near ? item.src : undefined}
            data-src={item.src}
            muted={muted}
            playsInline
            loop
            preload={active ? 'auto' : near ? 'metadata' : 'none'}
            className="w-full h-full object-cover"
          />
        ) : (
          <>
            {/* Tiny blurred placeholder shown until full image loads */}
            {!imgLoaded && (
              <img
                src={placeholderImg(item.src)}
                alt=""
                aria-hidden
                className="absolute inset-0 w-full h-full object-cover blur-lg scale-105"
              />
            )}
            {near && (
              <img
                src={optimizeImg(item.src, 1600, 92)}
                alt={`Casanova ${item.year} — ${index + 1}`}
                loading={active ? 'eager' : 'lazy'}
                decoding="async"
                fetchpriority={active ? 'high' : 'low'}
                onLoad={() => setImgLoaded(true)}
                className={`relative w-full h-full object-cover transition-opacity duration-500 ${
                  imgLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              />
            )}
          </>
        )}

        {/* Bottom gradient + caption */}
        <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none z-20">
          <div className="flex items-end justify-between gap-4">
            <div>
              <span className="eyebrow text-[#F5F0E8]/70 block mb-1">
                {language === 'fr' ? 'Casanova' : 'Casanova'} · {item.year}
              </span>
              <p className="font-display text-lg lg:text-xl text-[#F5F0E8] leading-snug">
                {item.type === 'video'
                  ? (language === 'fr' ? 'En mouvement' : 'In motion')
                  : (language === 'fr' ? 'Moment figé' : 'Still moment')}
              </p>
            </div>
            <span className="font-body text-xs text-[#F5F0E8]/60 tracking-widest lg:hidden">
              {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

// -----------------------------------------------------------------------------
// ScrollHint — animated chevron on the first slide; fades when user scrolls
// -----------------------------------------------------------------------------
const ScrollHint = ({ active, language }) => (
  <AnimatePresence>
    {active && (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.6 }}
        className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2 text-[#F5F0E8]/70"
        data-testid="scroll-hint"
      >
        <span className="eyebrow text-[10px]">
          {language === 'fr' ? 'Glissez' : 'Scroll'}
        </span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}
        >
          <ChevronDown size={18} />
        </motion.span>
      </motion.div>
    )}
  </AnimatePresence>
);

export default GalleryPage;
